// Typed wrappers around every admin-* edge function.
//
// Contract lives in docs/ops-api.md. Every function in this file maps 1:1 to
// an edge function in the main repo. When a new function is added there,
// add a matching wrapper here.
//
// The SPA never calls supabase.from(...) against tenant tables. It calls
// these wrappers, which call edge functions, which call SECURITY DEFINER
// RPCs. That is the only allowed data path.

import { SUPABASE_URL, supabase } from "./supabase";
import type { TrustTierArg } from "./zumTrust";

/**
 * The `tier` argument admin-company-zum-trust-override accepts. Sourced from
 * the ladder mirror so the wire contract and the display mirror cannot drift
 * to different tier vocabularies.
 */
export type ZumTrustTierArg = TrustTierArg;

class AdminApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
  ) {
    super(`admin api ${status}`);
    this.name = "AdminApiError";
  }
}

async function call<TReq, TRes>(fn: string, body: TReq): Promise<TRes> {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData.session?.access_token;
  if (!token) {
    throw new AdminApiError(401, { error: "no_session" });
  }

  const res = await fetch(`${SUPABASE_URL}/functions/v1/${fn}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body ?? {}),
  });

  const parsed = (await res.json().catch(() => ({}))) as unknown;

  if (!res.ok) {
    throw new AdminApiError(res.status, parsed);
  }
  return parsed as TRes;
}

// ─── Types (kept thin — refine alongside ops-api.md) ─────────────────────────

export type Window = "day" | "week" | "month" | "custom";

export interface WhoAmI {
  userId: string;
  email: string;
  grantedAt: string;
  isAdmin: true;
}

export interface TokenSummary {
  totals: {
    input_tokens: number;
    output_tokens: number;
    total_tokens: number;
    call_count: number;
    distinct_users: number;
    distinct_companies: number;
  };
  series: Array<{ day: string; total_tokens: number; call_count: number }>;
  generated_at: string;
}

export interface DeployLogEntry {
  id: string;
  deployed_at: string;
  commit_sha: string;
  image_tag: string | null;
  deployed_by: string | null;
  status: "success" | "failure" | "rolled_back";
  pending_before: string[];
  pending_after: string[];
  notes: string | null;
}

export interface ServerStatus {
  kuma: {
    heartbeat_at: string;
    monitors: Array<{
      name: string;
      status: "up" | "down" | "pending";
      uptime_24h_pct: number;
      avg_response_ms: number;
    }>;
  };
  containers: {
    snapshot_at: string;
    list: Array<{
      name: string;
      image: string;
      status: string;
      uptime: string;
      restart_count: number;
    }>;
  };
}

export interface MigrationsStatus {
  applied: Array<{ version: string; applied_at: string }>;
  pending: string[];
  snapshot_at: string;
  in_sync: boolean;
}

export interface SubscriptionSummary {
  by_tier: Array<{
    tier: string;
    active_count: number;
    trialing_count: number;
    /** CAD list price of the plan. */
    monthly_price_cents?: number | null;
    /** Already active_count x CAD base price (no add-ons, seats, or USD). */
    monthly_revenue_cents: number | null;
  }>;
  /** Additive (main repo 20270221000000); absent until the server migrates. */
  revenue?: SubscriptionRevenue;
  by_module?: Array<{
    module_code: string;
    scope: "company" | "user";
    active_count: number;
    pending_removal_count: number;
  }>;
  recent_signups_30d: number;
  recent_cancellations_30d: number;
  trial_to_active_rate_pct: number;
  series: Array<{ day: string; active_count: number; trialing_count: number }>;
  /** Additive (main repo 20270227000200); absent until the server migrates. */
  film_credit?: FilmCreditSummary;
}

/**
 * Film Credit Assist one-time production unlocks. `revenue_by_currency.amount`
 * is DOLLARS (numeric, 2 dp), a lifetime total — never part of MRR.
 */
export interface FilmCreditSummary {
  companies_with_module: number;
  productions_total: number;
  productions_unlocked: number;
  revenue_by_currency: Array<{ currency: string; amount: number }>;
}

/** Company-scoped module codes the app can grant (SELF is user-scoped). */
export type CompanyModuleCode =
  | "BOOKS"
  | "OPS"
  | "INVENTORY"
  | "PEOPLE"
  | "SOLO"
  | "FILM_CREDIT";

/** Display names for module codes. by_module is code-keyed — never index it by position. */
export const MODULE_LABELS: Record<string, string> = {
  BOOKS: "Books",
  OPS: "Ops",
  INVENTORY: "Inventory",
  PEOPLE: "People",
  SOLO: "Solo",
  FILM_CREDIT: "Film Credit Assist",
  SELF: "Self",
};

export interface FilmProduction {
  id: string;
  title: string;
  /** planning | in_production | post_production | completed | archived */
  status: string;
  /** One-time unlock payment time; null while locked. */
  paid_at: string | null;
  /** Dollars, 2 dp. */
  amount_paid: number | null;
  paid_currency: string | null;
}

export type BillingCurrency = "CAD" | "USD";

export interface ModuleDrift {
  module_code: string;
  count: number;
  companies: Array<{ company_id: string; name: string }>;
}

export interface SubscriptionRevenue {
  currencies: Record<
    BillingCurrency,
    {
      /** Net of in-effect discounts. */
      mrr_cents: number;
      /** Additive (20270222000000): before discounts. */
      gross_mrr_cents?: number;
      /** Additive (20270222000000): positive total of discount lines. */
      discount_cents?: number;
      subscriptions: number;
    }
  >;
  past_due_subscriptions: number;
  /** Additive (20270222000000): discounts with a known end within 90 days. */
  discounts_ending_90d?: Array<{
    subscription_id: string;
    company_id: string | null;
    company_name: string | null;
    product: string;
    ends_at: string;
    amount_cents: number;
    currency: BillingCurrency;
  }>;
  /** Additive (20270222000000): Stripe discount mirror coverage. */
  discount_sync?: {
    synced_subscriptions: number;
    unsynced_subscriptions: number;
    oldest_sync_at: string | null;
  };
  by_product: Array<{
    product: string;
    label: string;
    units_cad: number;
    units_usd: number;
    list_price_cents_cad: number | null;
    list_price_cents_usd: number | null;
    mrr_cents_cad: number;
    mrr_cents_usd: number;
    unpriced_units: number;
  }>;
  seats: {
    people_subscriptions: number;
    paid_seats: number;
    included_seats: number;
  };
  trialing_pipeline: {
    subscriptions: number;
    mrr_cents_cad: number;
    mrr_cents_usd: number;
  };
  unpriced_subscriptions: number;
  drift: {
    entitled_not_billed: ModuleDrift[];
    billed_not_entitled: ModuleDrift[];
    companies_without_subscription: number;
  };
}

export interface CompanyBilling {
  subscription_id: string;
  scope: "company" | "legacy_owner";
  tier: string;
  status: string;
  billing_currency: BillingCurrency;
  current_period_end: string | null;
  stripe_subscription_id: string | null;
  selected_modules: string[];
  monthly_revenue_cents: number | null;
  unpriced_lines: number;
  lines: Array<{
    product: string;
    units: number;
    amount_cents: number | null;
    /** Discount lines only: when the discount stops applying, if known. */
    ends_at?: string | null;
  }>;
  /** Additive (20270222000000): the mirrored Stripe discounts. */
  discounts?: StoredDiscount[];
  discounts_synced_at?: string | null;
}

export interface StoredDiscount {
  discount_id: string;
  coupon_id: string | null;
  coupon_name: string | null;
  promotion_code_id: string | null;
  kind: "promo" | "accountant_reward" | "coupon";
  percent_off: number | null;
  amount_off_cents: number | null;
  amount_currency: string | null;
  duration: "forever" | "once" | "repeating";
  duration_in_months: number | null;
  start: string | null;
  end: string | null;
  applies_to: string[] | null;
  /** Absent on rows synced before 20270223000000 (= subscription level). */
  scope?: "subscription" | "item";
  item_product?: string | null;
}

export interface CompaniesListResponse {
  total: number;
  companies: Array<{
    company_id: string;
    name: string;
    owner_email: string;
    tier: string;
    modules?: string[];
    member_count: number;
    /** Legacy: the owner plan's max_companies, not a seat count. */
    seat_count: number;
    paid_seat_count?: number;
    included_seat_count?: number;
    monthly_tokens: number;
    last_activity_at: string | null;
  }>;
}

export interface TokenByCompanyResponse {
  companies: Array<{
    company_id: string;
    company_name: string;
    tier: string;
    input_tokens: number;
    output_tokens: number;
    total_tokens: number;
    call_count: number;
    pct_of_total: number;
    sparkline: number[];
  }>;
}

export interface TokenByFunctionResponse {
  functions: Array<{
    function_name: string;
    call_count: number;
    total_tokens: number;
    input_tokens: number;
    output_tokens: number;
    cache_read_tokens: number;
    avg_tokens_per_call: number;
  }>;
}

export interface TokenByUserResponse {
  users: Array<{
    user_id: string;
    email: string;
    tier: string;
    self_tokens: number;
    company_tokens?: number;
    call_count: number;
  }>;
}

export interface CompanyDetail {
  company: {
    id: string;
    name: string;
    tier: string;
    modules?: Array<{
      module_code: string;
      status: string;
      deactivates_at: string | null;
    }>;
    created_at: string;
    province: string | null;
    business_number: string | null;
  };
  subscription:
    | {
        status: string;
        tier: string;
        current_period_end: string | null;
        cancel_at_period_end: boolean;
        monthly_revenue_cents: number | null;
      }
    | null;
  /** Additive (main repo 20270221000000): the company's governing subscription, priced. */
  billing?: CompanyBilling | null;
  members: Array<{
    user_id: string;
    email: string;
    role: string;
    joined_at: string;
  }>;
  monthly_tokens: { input: number; output: number; calls: number };
  token_series_30d: Array<{ day: string; tokens: number }>;
  by_function_30d: Array<{ function_name: string; tokens: number; calls: number }>;
  recent_audit_events_10: Array<{
    created_at: string;
    function_name: string;
    response_status: number;
  }>;
  /** Additive (main repo 20270227000200): Film Credit Assist productions, by created_at. */
  film_productions?: FilmProduction[];
}

export interface AuditLogResponse {
  total: number;
  events: Array<{
    id: string;
    created_at: string;
    user_id: string;
    user_email: string;
    function_name: string;
    request_summary: Record<string, unknown>;
    response_status: number;
    ip: string;
    user_agent: string;
    duration_ms: number;
  }>;
}

export type CompanyRole = "OWNER" | "HR" | "ACCOUNTANT" | "STANDARD_EMPLOYEE";

export type UserActionName =
  | "reset_password"
  | "ban"
  | "unban"
  | "signout_all"
  | "unenroll_mfa";

export interface UsersListResponse {
  total: number;
  users: Array<{
    user_id: string;
    email: string;
    full_name: string | null;
    tier: string;
    company_count: number;
    primary_company: string | null;
    email_confirmed: boolean;
    mfa_enabled: boolean;
    banned: boolean;
    last_sign_in_at: string | null;
    created_at: string;
  }>;
  facets: { tiers: string[] };
}

export interface UserDetailResponse {
  user: {
    id: string;
    email: string;
    full_name: string | null;
    created_at: string;
    last_sign_in_at: string | null;
    email_confirmed: boolean;
    banned: boolean;
    banned_until: string | null;
  };
  mfa_factors: Array<{
    id: string;
    factor_type: string;
    status: string;
    friendly_name: string | null;
    created_at: string;
  }>;
  memberships: Array<{
    company_id: string;
    company_name: string;
    role: string;
    joined_at: string;
  }>;
  subscription: {
    tier: string;
    status: string;
    current_period_end: string | null;
    trial_ends_at: string | null;
    stripe_subscription_id: string | null;
  } | null;
  recent_audit: Array<{
    created_at: string;
    function_name: string;
    response_status: number;
  }>;
}

// Outcome of the optional "notify the company owner" email that every company
// support action can request. Delivery is best-effort server-side: the action
// itself still succeeds when the email fails or is skipped.
export type EmailOutcome =
  | "sent"
  | "skipped_disabled"
  | "skipped_no_recipient"
  | "failed"
  | "not_requested";

export interface CompanyAiBudgetResetResponse {
  ok: true;
  company_id: string;
  month_start: string;
  previous_used_tokens: number;
  budget_limit: number | null;
  reset_id: string;
  reset_at: string;
  email: EmailOutcome;
}

export interface CompanyImportCooldownClearResponse {
  ok: true;
  company_id: string;
  waived_runs: number;
  previous_next_available: string | null;
  cleared_at: string;
  email: EmailOutcome;
}

export interface CompanyMigrationBudgetResetResponse {
  ok: true;
  company_id: string;
  previous_used_tokens: number;
  previous_call_count: number;
  budget_limit: number | null;
  reset_id: string;
  reset_at: string;
  email: EmailOutcome;
}

export interface CompanyAiBudgetOverrideResponse {
  ok: true;
  company_id: string;
  previous_override: number | null;
  new_override: number | null;
  plan_default: number | null;
  effective_limit: number | null;
  updated_at: string;
  email: EmailOutcome;
}

// Zum AR trust state for one company. The RPC's jsonb IS the 200 body — there
// is no wrapper envelope — and it is the FULL post-mutation state, so a
// successful write needs no follow-up read. A body carrying only company_id is
// a password-free status read; see docs/ops-api.md and
// docs/plans/zum-trust-support.md.
export interface CompanyZumTrustResponse {
  ok: true;
  company_id: string;
  /** null = auto-graduating. Otherwise "tier1".."tier6" or "custom". */
  trust_tier_override: string | null;
  /** Non-null only when the override is "custom". */
  trust_custom_per_pull_cap: number | null;
  trust_custom_exposure_cap: number | null;
  /** Non-null = this company is blocked from every AR send, at any amount. */
  ar_sends_held_at: string | null;
  ar_hold_reason: string | null;
  /** The LIVE ladder reading, returned even when a pin is in force. */
  auto_inputs: {
    completed_pull_count: number;
    first_completed_at: string | null;
    rolling_90d_exposure: number;
  };
}

export interface AccountantsSummaryResponse {
  totals: {
    accountants: number;
    activated: number;
    signed_up_only: number;
    servicing: number;
    client_companies: number;
    avg_clients: number | null;
    owners: number;
    owners_pct: number | null;
  };
  rewards: {
    qualifying_clients: number;
    total_monthly_credit_cents: number;
    avg_savings_pct: number | null;
    fully_covered: number;
    pending_review: number;
  };
  signups_series: Array<{ day: string; count: number }>;
}

export interface AccountantsListResponse {
  total: number;
  accountants: Array<{
    user_id: string;
    email: string;
    full_name: string | null;
    created_at: string;
    activated: boolean;
    client_count: number;
    qualifying_client_count: number;
    owned_company_count: number;
    own_monthly_sub_cents: number;
    monthly_credit_cents: number;
    savings_pct: number | null;
    review_status: string | null;
    last_active_at: string | null;
  }>;
}

// ─── Tax readiness (admin-tax-readiness-overview / -jurisdiction) ────────────
//
// Contract: docs/ops-api.md §admin-tax-readiness-overview and
// §admin-tax-readiness-jurisdiction; field meanings in the main repo's
// .claude/rules/data-contracts.md "Tax readiness model". Read-only: nothing in
// the console writes reference data. Counts only — no tenant records.

export type TaxCountry = "CA" | "US";

export type TaxReadinessStatus =
  | "VERIFIED"
  | "PARTIAL"
  | "RESEARCHED"
  | "MISSING"
  | "NOT_APPLICABLE"
  | "NOT_SUPPORTED";

export type TaxReadinessSeverity = "BLOCKER" | "WARNING" | "INFO" | "NONE";

export type TaxReadinessGate = "HARD" | "SOFT" | "NONE";

export type TaxRuntimeEffect =
  | "BLOCKS_PAYROLL"
  | "BLOCKS_FILING"
  | "SILENT_ZERO"
  | "SILENT_STALE"
  | "ADVISORY"
  | "NOT_SUPPORTED";

/** One jurisdiction × stream × tax year. `jurisdiction` is a 2-letter code or "FED". */
export interface TaxMatrixRow {
  country: TaxCountry;
  jurisdiction: string;
  tax_year: number;
  stream: string;
  status: TaxReadinessStatus;
  severity: TaxReadinessSeverity;
  gate: TaxReadinessGate;
  runtime_effect: TaxRuntimeEffect;
  enablement_on: boolean;
  /** Rate kinds; for tax_forms the form codes (T2 first), for slip_templates t4 / t4a. */
  required_kinds: string[];
  verified_kinds: string[];
  researched_kinds: string[];
  missing_kinds: string[];
  tenant_companies: number;
  tenant_employees: number;
  /**
   * Newest sign-off for one of the cell's required kinds, from the stream's source
   * table; always null for a stream with no kinds (code, forms, tax tables).
   */
  latest_signoff: TaxRateSignoff | null;
}

/** A rate sign-off. The RPCs return this explicit column allowlist, never the whole row. */
export interface TaxRateSignoff {
  country: TaxCountry;
  jurisdiction: string;
  tax_year: number;
  kind: string;
  signoff_type: string;
  signed_on: string;
  migration_version: string | null;
  /** The table the signed-off row lives in (payroll_rate_config, …); absent on older backends. */
  table_name?: string | null;
  /**
   * Not in the P0 allowlist — adding them is a founder decision and a change to
   * both RPCs. Rendered only when the backend sends them.
   */
  signed_by?: string | null;
  checked_against?: string | null;
}

/** One row of tax_readiness_stream, as the overview returns it (P1). */
export interface TaxReadinessStreamMeta {
  country: TaxCountry;
  stream: string;
  label: string;
  /** The plain sentence of what a tenant experiences when this stream is not ready. */
  tenant_effect: string;
  gate: TaxReadinessGate;
  runtime_effect: TaxRuntimeEffect;
  sort_order: number;
}

/**
 * A consistency finding (P1):
 *   C3 — consecutive-year payloads byte-identical where an indexed value is expected;
 *   C4 — a (jurisdiction, kind) has a row for year N-1 but not N (N <= current year);
 *   C9 — a code-manifest entry's max year trails the DB's max tax_year for the stream.
 */
export interface TaxConsistencyCheck {
  id: string;
  severity: "WARNING" | "INFO";
  country: TaxCountry;
  jurisdiction: string | null;
  tax_year: number | null;
  stream: string | null;
  message: string;
}

/** Summary of the code manifest the edge function passed (P1). */
export interface TaxCodeManifestSummary {
  generated_at: string | null;
  /**
   * `git rev-parse HEAD` when the manifest was generated — the PARENT of the commit
   * that carries the file, so it never equals deploy_sha. Information only.
   */
  source_git_sha: string | null;
  /** sha256 of the manifest entries — identifies what the deployed manifest says. */
  content_hash: string | null;
  /** Latest deploy_log.commit_sha, or null when nothing is logged. */
  deploy_sha: string | null;
  /** There is no "stale" flag: CI's tax-manifest:check is the only staleness signal. */
  entry_count: number;
}

export type TaxManifestVerification =
  | "VERIFIED"
  | "RESEARCHED_UNVERIFIED"
  | "MIXED"
  | "UNKNOWN";

/** One CODE_MANIFEST entry — reference data that lives in code rather than a table. */
export interface TaxManifestEntry {
  module: string;
  stream: string;
  country: TaxCountry;
  /** ["FED"] (or empty) for a federal entry. */
  jurisdictions: string[];
  /** Empty for unversioned data. */
  years: number[];
  verification: TaxManifestVerification;
  source: string;
  effective_from?: string | null;
}

export interface TaxCalendarRow {
  country: TaxCountry;
  stream: string;
  publisher: string;
  publication_month: number | null;
  /** "YYYY-MM-01" — the publication that feeds next year's data. */
  expected_publication: string | null;
  /** Gate of the stream. Only a HARD gate has a hard deadline. */
  gate: TaxReadinessGate;
  /** "<current+1>-01-01" on a HARD gate; null for SOFT / NONE streams. */
  hard_deadline: string | null;
  next_year_status: TaxReadinessStatus;
}

export interface TaxReadinessOverview {
  generated_at: string;
  /** "YYYY-MM-DD" — the server's date (platform-level, not a company day). */
  server_today: string;
  /** [year ?? current, current + 1], distinct, ascending. */
  years: number[];
  kpis: {
    blockers: number;
    warnings: number;
    next_hard_deadline: { date: string; country: TaxCountry; stream: string } | null;
  };
  matrix: TaxMatrixRow[];
  calendar: TaxCalendarRow[];
  /** tax_readiness_stream rows; [] from a backend that predates P1. */
  streams: TaxReadinessStreamMeta[];
  /** Consistency findings; [] from a backend that predates P1. */
  checks: TaxConsistencyCheck[];
  /** null when the edge function passed no manifest (or predates P1). */
  manifest: TaxCodeManifestSummary | null;
  last_snapshot: { snapshot_at: string; blockers: number; warnings: number } | null;
}

export interface TaxJurisdictionStream extends TaxMatrixRow {
  label: string;
  /** The plain sentence of what a tenant experiences when this stream is not ready. */
  tenant_effect: string;
}

export interface TaxRateRow {
  tax_year: number;
  kind: string;
  verification_status: "VERIFIED" | "RESEARCHED_UNVERIFIED";
  source: string | null;
  notes: string | null;
  created_at: string;
}

export interface TaxEsRule {
  tax_year: number;
  kind: string;
  verification_status: string;
}

export interface TaxReadinessJurisdiction {
  jurisdiction: {
    country: TaxCountry;
    code: string;
    label: string;
    flags: {
      onboarding_enabled: boolean;
      sales_tax_enabled: boolean;
      payroll_enabled: boolean;
      advisory_enabled: boolean;
      employment_standards_enabled: boolean;
    };
    notes: string | null;
  };
  /** This code's cells + the country's FED cells, both years, stream sort order then tax year. */
  streams: TaxJurisdictionStream[];
  /** tax_year DESC, kind. */
  rate_rows: TaxRateRow[];
  employment_standards: {
    rules: TaxEsRule[];
    /** Keyed by "YYYY". */
    holidays_by_year: Record<string, { total: number; verified: number }>;
  };
  /** DESC; [] for US. */
  personal_tax_years: number[];
  /** DESC; [] for US. */
  corporate_tax_years: number[];
  exposure: { companies: number; employees: number };
  waitlist: number;
  /** Newest first; [] when no sign-off is recorded for the jurisdiction. */
  signoffs: TaxRateSignoff[];
  /** Code-manifest entries touching this jurisdiction or FED; [] before P1. */
  manifest_entries: TaxManifestEntry[];
}

// ─── Tax rate verification (docs/ops-api.md §admin-tax-readiness-verify) ────
//
// Computes only. The function decodes the reference rows, runs the payroll
// engine on SYNTHETIC fixtures, diffs against the prior year and returns the
// TEXT of a sign-off + VERIFIED-flip migration. Nothing is written: the
// operator copies flip_migration_sql into a PR in the main repo.

export type TaxSignoffType = "cpa" | "founder_attestation" | "relayed_cpa";

/** The sign-off form. Echoed into flip_migration_sql only — never stored. */
export interface TaxVerifySignoff {
  signed_by: string;
  signed_by_role: string;
  signoff_type: TaxSignoffType;
  /** Required when signoff_type is "cpa" or "relayed_cpa". */
  cpa_name?: string;
  /** "YYYY-MM-DD". */
  signed_on: string;
  checked_against: string;
}

export interface TaxVerifyRow {
  kind: string;
  /** "YYYY-MM-DD". */
  effective_from: string;
  verification_status: "VERIFIED" | "RESEARCHED_UNVERIFIED";
  decode_ok: boolean;
  decode_error: string | null;
  source: string | null;
  /** RESEARCHED_UNVERIFIED and readable — the only rows a flip may name. */
  flippable: boolean;
  /** Named in flip_migration_sql (the flip_rows sent, else the default: all but employer levies). */
  in_flip: boolean;
}

/** A (kind, effective_from) pair to include in the flip migration. */
export interface TaxVerifyFlipKey {
  kind: string;
  /** "YYYY-MM-DD". */
  effective_from: string;
}

/**
 * A row dated in by as_of that live runs do not use yet: unverified, while an
 * earlier VERIFIED row of the same kind still applies.
 */
export interface TaxVerifyHeldBack {
  jurisdiction: string;
  kind: string;
  /** effective_from of the unverified row the fixtures price on. */
  latest: string;
  /** effective_from of the VERIFIED row a live run still uses. */
  served: string;
}

export interface TaxVerifyFixtureResult {
  federal: number;
  provincial_or_state: number;
  cpp_or_ss: number;
  ei_or_medicare: number;
  employer: Record<string, number>;
  net: number;
  /** The engine's own breakdown (cpp/cpp2/ei/qpip/insurable earnings; SS/Medicare/state wages). */
  detail: Record<string, number>;
}

export interface TaxVerifyFixture {
  id: string;
  description: string;
  gross: number;
  pay_frequency: string;
  filing_status?: string | null;
  claim_code?: string | null;
  ytd_context: Record<string, unknown>;
  result: TaxVerifyFixtureResult | null;
  error: string | null;
}

export interface TaxVerifyDiff {
  kind: string;
  /** "" for a whole-payload note. */
  field_path: string;
  previous: unknown;
  current: unknown;
  note: string;
}

export interface TaxReadinessVerifyResponse {
  generated_at: string;
  country: TaxCountry;
  jurisdiction: string;
  tax_year: number;
  /** "YYYY-MM-DD": the pay date the fixtures and loader_would_serve price on. */
  as_of: string;
  /** [] when the fixtures and a live run read the same rows. */
  held_back: TaxVerifyHeldBack[];
  rows: TaxVerifyRow[];
  loader_would_serve: { served: boolean; reason: string };
  fixtures: TaxVerifyFixture[];
  prior_year_diff: TaxVerifyDiff[];
  /** "" when no row is flippable. */
  flip_migration_sql: string;
}

// ─── Normalizers ─────────────────────────────────────────────────────────────
//
// Thin boundary guards. The admin-* edge functions return the documented
// contract shape directly — the VPS ops-cron reshapes `docker ps` and merges
// the two Kuma endpoints before INSERT (backlog 2.10 / 2.12, fixed upstream
// in the main repo). These guards only default a missing timestamp to epoch
// (so Server.tsx's "cron not running" UX still triggers on an empty cache)
// and coerce the array fields to [] so consumers can .map()/.reduce() without
// a null check.

function normalizeMigrationsStatus(raw: MigrationsStatus): MigrationsStatus {
  return {
    applied: Array.isArray(raw.applied) ? raw.applied : [],
    pending: Array.isArray(raw.pending) ? raw.pending : [],
    snapshot_at: raw.snapshot_at ?? new Date(0).toISOString(),
    in_sync: typeof raw.in_sync === "boolean" ? raw.in_sync : false,
  };
}

function normalizeServerStatus(raw: ServerStatus): ServerStatus {
  const kuma = (raw.kuma ?? {}) as Partial<ServerStatus["kuma"]>;
  const containers = (raw.containers ?? {}) as Partial<
    ServerStatus["containers"]
  >;
  return {
    kuma: {
      heartbeat_at: kuma.heartbeat_at ?? new Date(0).toISOString(),
      monitors: Array.isArray(kuma.monitors) ? kuma.monitors : [],
    },
    containers: {
      snapshot_at: containers.snapshot_at ?? new Date(0).toISOString(),
      list: Array.isArray(containers.list) ? containers.list : [],
    },
  };
}

// Tax readiness guards: same posture as the snapshot guards above — coerce the
// array fields to [] and the counts to 0 so the pages can .map()/.filter()
// without null checks, and default a missing generated_at to epoch.

function arr<T>(v: unknown): T[] {
  return Array.isArray(v) ? (v as T[]) : [];
}

function num(v: unknown): number {
  return typeof v === "number" && Number.isFinite(v) ? v : 0;
}

function str(v: unknown): string | null {
  return typeof v === "string" && v !== "" ? v : null;
}

function normalizeTaxStreamMeta(raw: TaxReadinessStreamMeta): TaxReadinessStreamMeta {
  return {
    ...raw,
    label: str(raw.label) ?? raw.stream,
    tenant_effect: typeof raw.tenant_effect === "string" ? raw.tenant_effect : "",
    sort_order: num(raw.sort_order),
  };
}

function normalizeTaxCheck(raw: TaxConsistencyCheck): TaxConsistencyCheck {
  return {
    ...raw,
    id: typeof raw.id === "string" ? raw.id : "",
    severity: raw.severity === "WARNING" ? "WARNING" : "INFO",
    jurisdiction: str(raw.jurisdiction),
    tax_year:
      typeof raw.tax_year === "number" && Number.isFinite(raw.tax_year)
        ? raw.tax_year
        : null,
    stream: str(raw.stream),
    message: typeof raw.message === "string" ? raw.message : "",
  };
}

/** P0 sent `manifest: null`; anything that is not an object reads as null. */
function normalizeTaxManifestSummary(raw: unknown): TaxCodeManifestSummary | null {
  if (raw == null || typeof raw !== "object" || Array.isArray(raw)) return null;
  const m = raw as Partial<Record<keyof TaxCodeManifestSummary, unknown>>;
  return {
    generated_at: str(m.generated_at),
    source_git_sha: str(m.source_git_sha),
    content_hash: str(m.content_hash),
    deploy_sha: str(m.deploy_sha),
    entry_count: num(m.entry_count),
  };
}

function normalizeTaxManifestEntry(raw: TaxManifestEntry): TaxManifestEntry {
  return {
    ...raw,
    jurisdictions: arr<string>(raw.jurisdictions),
    years: arr<number>(raw.years),
    verification: raw.verification ?? "UNKNOWN",
    source: typeof raw.source === "string" ? raw.source : "",
  };
}

function normalizeTaxMatrixRow<T extends TaxMatrixRow>(raw: T): T {
  return {
    ...raw,
    enablement_on: raw.enablement_on === true,
    required_kinds: arr<string>(raw.required_kinds),
    verified_kinds: arr<string>(raw.verified_kinds),
    researched_kinds: arr<string>(raw.researched_kinds),
    missing_kinds: arr<string>(raw.missing_kinds),
    tenant_companies: num(raw.tenant_companies),
    tenant_employees: num(raw.tenant_employees),
    latest_signoff: raw.latest_signoff ?? null,
  };
}

/**
 * Calendar rows carry their stream's gate. A backend that predates the `gate`
 * key gets it from the matrix, and a non-HARD stream never shows a hard
 * deadline (SOFT / NONE streams have none).
 */
function normalizeTaxCalendar(
  calendar: TaxCalendarRow[],
  matrix: TaxMatrixRow[],
): TaxCalendarRow[] {
  return calendar.map((c) => {
    const gate: TaxReadinessGate =
      c.gate ??
      matrix.find((m) => m.country === c.country && m.stream === c.stream)?.gate ??
      "NONE";
    return {
      ...c,
      gate,
      hard_deadline: gate === "HARD" ? (c.hard_deadline ?? null) : null,
    };
  });
}

function normalizeTaxReadinessOverview(
  raw: TaxReadinessOverview,
): TaxReadinessOverview {
  const kpis = (raw.kpis ?? {}) as Partial<TaxReadinessOverview["kpis"]>;
  return {
    generated_at: raw.generated_at ?? new Date(0).toISOString(),
    server_today: raw.server_today ?? new Date().toISOString().slice(0, 10),
    years: arr<number>(raw.years),
    kpis: {
      blockers: num(kpis.blockers),
      warnings: num(kpis.warnings),
      next_hard_deadline: kpis.next_hard_deadline ?? null,
    },
    matrix: arr<TaxMatrixRow>(raw.matrix).map(normalizeTaxMatrixRow),
    calendar: normalizeTaxCalendar(
      arr<TaxCalendarRow>(raw.calendar),
      arr<TaxMatrixRow>(raw.matrix),
    ),
    streams: arr<TaxReadinessStreamMeta>(raw.streams).map(normalizeTaxStreamMeta),
    checks: arr<TaxConsistencyCheck>(raw.checks).map(normalizeTaxCheck),
    manifest: normalizeTaxManifestSummary(raw.manifest),
    last_snapshot: raw.last_snapshot ?? null,
  };
}

function normalizeTaxReadinessJurisdiction(
  raw: TaxReadinessJurisdiction,
): TaxReadinessJurisdiction {
  const j = (raw.jurisdiction ?? {}) as Partial<
    TaxReadinessJurisdiction["jurisdiction"]
  >;
  const flags = (j.flags ?? {}) as Partial<
    TaxReadinessJurisdiction["jurisdiction"]["flags"]
  >;
  const es = (raw.employment_standards ?? {}) as Partial<
    TaxReadinessJurisdiction["employment_standards"]
  >;
  const exposure = (raw.exposure ?? {}) as Partial<
    TaxReadinessJurisdiction["exposure"]
  >;
  return {
    jurisdiction: {
      country: j.country ?? "CA",
      code: j.code ?? "",
      label: j.label ?? j.code ?? "",
      flags: {
        onboarding_enabled: flags.onboarding_enabled === true,
        sales_tax_enabled: flags.sales_tax_enabled === true,
        payroll_enabled: flags.payroll_enabled === true,
        advisory_enabled: flags.advisory_enabled === true,
        employment_standards_enabled: flags.employment_standards_enabled === true,
      },
      notes: j.notes ?? null,
    },
    streams: arr<TaxJurisdictionStream>(raw.streams).map(normalizeTaxMatrixRow),
    rate_rows: arr<TaxRateRow>(raw.rate_rows),
    employment_standards: {
      rules: arr<TaxEsRule>(es.rules),
      holidays_by_year:
        es.holidays_by_year && typeof es.holidays_by_year === "object"
          ? es.holidays_by_year
          : {},
    },
    personal_tax_years: arr<number>(raw.personal_tax_years),
    corporate_tax_years: arr<number>(raw.corporate_tax_years),
    exposure: {
      companies: num(exposure.companies),
      employees: num(exposure.employees),
    },
    waitlist: num(raw.waitlist),
    signoffs: arr<TaxRateSignoff>(raw.signoffs),
    manifest_entries: arr<TaxManifestEntry>(raw.manifest_entries).map(
      normalizeTaxManifestEntry,
    ),
  };
}

function normalizeTaxVerifyResult(raw: unknown): TaxVerifyFixtureResult | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Partial<TaxVerifyFixtureResult>;
  const employer: Record<string, number> = {};
  if (r.employer && typeof r.employer === "object") {
    for (const [k, v] of Object.entries(r.employer)) employer[k] = num(v);
  }
  const detail: Record<string, number> = {};
  if (r.detail && typeof r.detail === "object") {
    for (const [k, v] of Object.entries(r.detail)) detail[k] = num(v);
  }
  return {
    federal: num(r.federal),
    provincial_or_state: num(r.provincial_or_state),
    cpp_or_ss: num(r.cpp_or_ss),
    ei_or_medicare: num(r.ei_or_medicare),
    employer,
    net: num(r.net),
    detail,
  };
}

function normalizeTaxReadinessVerify(
  raw: TaxReadinessVerifyResponse,
): TaxReadinessVerifyResponse {
  const lws = (raw.loader_would_serve ?? {}) as Partial<
    TaxReadinessVerifyResponse["loader_would_serve"]
  >;
  return {
    generated_at: raw.generated_at ?? new Date(0).toISOString(),
    country: raw.country ?? "CA",
    jurisdiction: raw.jurisdiction ?? "",
    tax_year: num(raw.tax_year),
    as_of: typeof raw.as_of === "string" ? raw.as_of : "",
    held_back: arr<TaxVerifyHeldBack>(raw.held_back).map((h) => ({
      jurisdiction: h.jurisdiction ?? "",
      kind: h.kind ?? "",
      latest: h.latest ?? "",
      served: h.served ?? "",
    })),
    rows: arr<TaxVerifyRow>(raw.rows).map((r) => ({
      kind: r.kind ?? "",
      effective_from: r.effective_from ?? "",
      verification_status:
        r.verification_status === "VERIFIED" ? "VERIFIED" : "RESEARCHED_UNVERIFIED",
      decode_ok: r.decode_ok === true,
      decode_error: str(r.decode_error),
      source: str(r.source),
      flippable: r.flippable === true,
      in_flip: r.in_flip === true,
    })),
    loader_would_serve: {
      served: lws.served === true,
      reason: typeof lws.reason === "string" ? lws.reason : "",
    },
    fixtures: arr<TaxVerifyFixture>(raw.fixtures).map((f) => ({
      id: f.id ?? "",
      description: f.description ?? "",
      gross: num(f.gross),
      pay_frequency: f.pay_frequency ?? "",
      filing_status: str(f.filing_status),
      claim_code: str(f.claim_code),
      ytd_context:
        f.ytd_context && typeof f.ytd_context === "object" ? f.ytd_context : {},
      result: normalizeTaxVerifyResult(f.result),
      error: str(f.error),
    })),
    prior_year_diff: arr<TaxVerifyDiff>(raw.prior_year_diff).map((d) => ({
      kind: d.kind ?? "",
      field_path: typeof d.field_path === "string" ? d.field_path : "",
      previous: d.previous ?? null,
      current: d.current ?? null,
      note: typeof d.note === "string" ? d.note : "",
    })),
    flip_migration_sql:
      typeof raw.flip_migration_sql === "string" ? raw.flip_migration_sql : "",
  };
}

// ─── Wrappers ────────────────────────────────────────────────────────────────

export const adminApi = {
  whoami: () => call<Record<string, never>, WhoAmI>("admin-whoami", {}),

  tokenSummary: (args: { window: Window; start?: string; end?: string; days?: number }) =>
    call<typeof args, TokenSummary>("admin-token-summary", args),

  deployLog: (args: { limit?: number } = {}) =>
    call<typeof args, { deploys: DeployLogEntry[] }>("admin-deploy-log", args),

  serverStatus: () =>
    call<Record<string, never>, ServerStatus>("admin-server-status", {}).then(
      normalizeServerStatus,
    ),

  migrationsStatus: (args: { limit?: number } = {}) =>
    call<typeof args, MigrationsStatus>("admin-migrations-status", args).then(
      normalizeMigrationsStatus,
    ),

  subscriptionSummary: () =>
    call<Record<string, never>, SubscriptionSummary>("admin-subscription-summary", {}),

  companiesList: (args: {
    search?: string;
    tier?: string;
    module?: string;
    sort?: "name" | "tokens_month" | "members" | "last_activity";
    limit?: number;
    offset?: number;
  } = {}) => call<typeof args, CompaniesListResponse>("admin-companies-list", args),

  companyDetail: (args: { company_id: string }) =>
    call<typeof args, CompanyDetail>("admin-company-detail", args),

  tokenByCompany: (args: {
    window: Window;
    start?: string;
    end?: string;
    limit?: number;
  }) => call<typeof args, TokenByCompanyResponse>("admin-token-by-company", args),

  tokenByFunction: (args: { window: Window; start?: string; end?: string }) =>
    call<typeof args, TokenByFunctionResponse>("admin-token-by-function", args),

  tokenByUser: (args: {
    window: Window;
    start?: string;
    end?: string;
    limit?: number;
    includeCompanyTotals?: boolean;
  }) => call<typeof args, TokenByUserResponse>("admin-token-by-user", args),

  auditLog: (args: {
    user_id?: string;
    function_name?: string;
    start?: string;
    end?: string;
    status?: number;
    limit?: number;
    offset?: number;
  } = {}) => call<typeof args, AuditLogResponse>("admin-audit-log", args),

  usersList: (args: {
    q?: string;
    tier?: string;
    company_id?: string;
    status?: "active" | "banned";
    sort?: "email" | "created" | "last_sign_in";
    limit?: number;
    offset?: number;
  } = {}) => call<typeof args, UsersListResponse>("admin-users-list", args),

  userDetail: (args: { user_id: string }) =>
    call<typeof args, UserDetailResponse>("admin-user-detail", args),

  userAction: (args: { user_id: string; action: UserActionName }) =>
    call<typeof args, { ok: true; action: string; affected?: number }>(
      "admin-user-action",
      args,
    ),

  userMembership: (args: {
    user_id: string;
    company_id: string;
    op: "set_role" | "add" | "remove";
    role?: CompanyRole;
  }) => call<typeof args, { ok: true; op: string }>("admin-user-membership", args),

  companyAiBudgetReset: (args: {
    company_id: string;
    password: string;
    reason?: string;
    notify?: boolean;
  }) =>
    call<typeof args, CompanyAiBudgetResetResponse>(
      "admin-company-ai-budget-reset",
      args,
    ),

  companyImportCooldownClear: (args: {
    company_id: string;
    password: string;
    reason?: string;
    notify?: boolean;
  }) =>
    call<typeof args, CompanyImportCooldownClearResponse>(
      "admin-company-import-cooldown-clear",
      args,
    ),

  companyMigrationBudgetReset: (args: {
    company_id: string;
    password: string;
    reason?: string;
    notify?: boolean;
  }) =>
    call<typeof args, CompanyMigrationBudgetResetResponse>(
      "admin-company-migration-budget-reset",
      args,
    ),

  companyAiBudgetOverride: (args: {
    company_id: string;
    password: string;
    budget_tokens: number | null;
    reason?: string;
    notify?: boolean;
  }) =>
    call<typeof args, CompanyAiBudgetOverrideResponse>(
      "admin-company-ai-budget-override",
      args,
    ),

  // Read + write in one endpoint. OMIT a field to leave it alone — never send
  // an explicit null, which the RPC reads as "clear this". JSON.stringify drops
  // undefined values, so an optional arg left off the object never reaches the
  // wire. `password` is REQUIRED whenever tier / custom_per_pull /
  // custom_exposure / clear_hold:true is present (mutation-only step-up); a
  // body of just { company_id } is the password-free status read.
  companyZumTrustOverride: (args: {
    company_id: string;
    tier?: ZumTrustTierArg;
    custom_per_pull?: number;
    custom_exposure?: number;
    clear_hold?: boolean;
    reason?: string;
    password?: string;
  }) =>
    call<typeof args, CompanyZumTrustResponse>(
      "admin-company-zum-trust-override",
      args,
    ),

  accountantsSummary: () =>
    call<Record<string, never>, AccountantsSummaryResponse>("admin-accountants-summary", {}),

  accountantsList: (args: {
    search?: string;
    sort?: "clients" | "signup" | "name" | "credit" | "last_active";
    limit?: number;
    offset?: number;
  } = {}) => call<typeof args, AccountantsListResponse>("admin-accountants-list", args),

  // Platform tax readiness — both read-only. `year` omitted = the server's
  // current year; the response always carries that year and the next.
  taxReadinessOverview: (args: { year?: number } = {}) =>
    call<typeof args, TaxReadinessOverview>(
      "admin-tax-readiness-overview",
      args,
    ).then(normalizeTaxReadinessOverview),

  taxReadinessJurisdiction: (args: { country: TaxCountry; code: string }) =>
    call<typeof args, TaxReadinessJurisdiction>(
      "admin-tax-readiness-jurisdiction",
      args,
    ).then(normalizeTaxReadinessJurisdiction),

  // Password step-up ALWAYS (no read-only mode). 403 reauth_failed / 429
  // rate_limited come back as AdminApiError with that status — the caller
  // keeps them inline in the ReauthDialog, like ZumTrustCard. Never writes.
  taxReadinessVerify: (args: {
    country: TaxCountry;
    jurisdiction: string;
    tax_year: number;
    password: string;
    signoff?: TaxVerifySignoff;
    /** Rows to name in the flip migration; omitted ⇒ every flippable row except employer levies. */
    flip_rows?: TaxVerifyFlipKey[];
  }) =>
    call<typeof args, TaxReadinessVerifyResponse>(
      "admin-tax-readiness-verify",
      args,
    ).then(normalizeTaxReadinessVerify),
};

export { AdminApiError };
